import * as React from 'react';
import { cn } from '@/lib/utils';

export type StickyNoteTone = 'yellow' | 'teal' | 'coral';

export interface StickyNoteProps extends React.HTMLAttributes<HTMLDivElement> {
  tilt?: number;
  tone?: StickyNoteTone;
  tape?: boolean;
}

const toneClass: Record<StickyNoteTone, string> = {
  yellow: 'border-[oklch(0.78_0.10_85)] bg-[oklch(0.96_0.07_88)]',
  teal: 'border-fd-teal/30 bg-fd-teal-soft',
  coral: 'border-fd-coral/30 bg-fd-coral-soft',
};

export const StickyNote = React.forwardRef<HTMLDivElement, StickyNoteProps>(
  ({ tilt = -0.6, tone = 'yellow', tape = true, className, style, children, ...rest }, ref) => {
    const useAsset = tone === 'yellow' && tape;

    return (
      <div className={cn('relative', className)} ref={ref} {...rest}>
        {useAsset ? (
          <img
            aria-hidden
            src="/character-system/scribble/sticky-note-yellow-v1.png"
            className="pointer-events-none absolute inset-0 h-full w-full select-none object-fill"
            draggable={false}
          />
        ) : tape ? (
          <span
            aria-hidden
            className="absolute -top-2 left-1/2 z-10 hidden h-3 w-16 -translate-x-1/2 rotate-[-3deg] rounded-[1px] bg-fd-text/15 sm:block"
            style={{ boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.04)' }}
          />
        ) : null}
        <aside
          className={cn(
            'relative z-10 rounded-[6px] border px-4 py-4',
            useAsset && 'px-7 pb-5 pt-12',
            useAsset ? 'border-transparent bg-transparent' : toneClass[tone],
          )}
          style={{
            transform: `rotate(${tilt}deg)`,
            boxShadow:
              '0 1px 0 rgba(60, 40, 0, 0.06), 4px 6px 14px -8px rgba(60, 40, 0, 0.35)',
            ...style,
          }}
        >
          {children}
        </aside>
      </div>
    );
  },
);
StickyNote.displayName = 'StickyNote';
